import { Segmented, Upload } from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { InboxOutlined } from '@ant-design/icons'

const { Dragger } = Upload

const TB_ACCEPT = '.xlsx,.csv,.pdf'

export type TbSource = 'upload' | 'xero'

type TrialBalancePanelProps = {
  hasXeroAssets: boolean
  assets: { account: string; balance: number }[]
  indexFileList: UploadFile[]
  onIndexFileListChange: (fileList: UploadFile[]) => void
  tbSource: TbSource
  onTbSourceChange: (source: TbSource) => void
  disabled?: boolean
}

export function TrialBalancePanel({
  hasXeroAssets,
  assets,
  indexFileList,
  onIndexFileListChange,
  tbSource,
  onTbSourceChange,
  disabled = false,
}: TrialBalancePanelProps) {
  return (
    <div className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-dashed border-slate-200 bg-white p-4">
      <div className="mb-4 flex min-h-[116px] shrink-0 flex-col">
        <h3 className="mb-3 text-base font-semibold text-slate-800">Trial Balance</h3>
        <Segmented
          value={tbSource}
          onChange={(val) => onTbSourceChange(val as TbSource)}
          disabled={disabled}
          options={[
            { label: 'File Upload', value: 'upload' },
            { label: 'Fetch From Xero', value: 'xero', disabled: !hasXeroAssets },
          ]}
        />
        {tbSource === 'xero' && !hasXeroAssets && (
          <p className="mt-1 text-xs text-amber-500">No Xero data available for this organisation</p>
        )}
        <p className="mt-4 text-sm text-slate-500">
          {tbSource === 'xero'
            ? 'Assets fetched from Xero trial balance'
            : 'Upload one trial balance file (Excel, CSV, or PDF)'}
        </p>
      </div>
      {tbSource === 'xero' ? (
        <div
          className={`max-h-[60vh] min-h-0 flex-1 overflow-auto rounded border border-slate-100 ${
            disabled ? 'pointer-events-none opacity-60' : ''
          }`}
        >
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Account</th>
                <th className="px-3 py-2 text-right font-medium text-slate-600">Balance</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((row, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="max-w-[200px] truncate px-3 py-2 text-slate-800" title={row.account}>
                    {row.account}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-slate-700">
                    {row.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="min-h-[360px] flex-1 overflow-auto pb-14">
          <Dragger
            accept={TB_ACCEPT}
            fileList={indexFileList}
            maxCount={1}
            disabled={disabled}
            beforeUpload={() => false}
            onChange={({ fileList }) => onIndexFileListChange(fileList)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined className="text-3xl text-slate-400" />
            </p>
            <p className="ant-upload-text">Drop file here or browse</p>
            <p className="ant-upload-hint text-xs text-slate-500">.xlsx, .csv, .pdf supported</p>
          </Dragger>
        </div>
      )}
    </div>
  )
}
